import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureFlagsProvider, useFeatureFlags } from './context/FeatureFlagsContext';
import { Toaster } from 'sonner';
import Sidebar from './components/Sidebar';
import ChangePasswordModal from './components/ChangePasswordModal';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsPage from './pages/TermsPage';
import DashboardPage from './pages/DashboardPage';
import StudentsPage from './pages/StudentsPage';
import StudentProfilePage from './pages/StudentProfilePage';
import AddStudentPage from './pages/AddStudentPage';
import ReportsPage from './pages/ReportsPage';
import ExportsPage from './pages/ExportsPage';
import SettingsPage from './pages/SettingsPage';
import QuickEntryPage from './pages/QuickEntryPage';
import UsersPage from './pages/UsersPage';
import ImportPage from './pages/ImportPage';
import SchoolsPage from './pages/SchoolsPage';
import StaffDirectoryPage from './pages/StaffDirectoryPage';
import AssessmentsComingSoonPage from './pages/AssessmentsComingSoonPage';
import AssessmentsAdminPreviewPage from './pages/AssessmentsAdminPreviewPage';
import ExplorePage from './pages/ExplorePage';
import './App.css';

// Protected Route Wrapper - includes terms acceptance check
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword, termsAccepted } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change for users with temp passwords
  if (mustChangePassword) {
    return <ChangePasswordModal />;
  }

  // Force terms acceptance if not accepted
  if (!termsAccepted) {
    return <Navigate to="/terms" replace />;
  }

  return children;
};

// Role-protected route wrapper - includes terms acceptance check
const RoleProtectedRoute = ({ children, requiredRoles }) => {
  const { isAuthenticated, loading, user, mustChangePassword, termsAccepted } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password change for users with temp passwords
  if (mustChangePassword) {
    return <ChangePasswordModal />;
  }

  // Force terms acceptance if not accepted
  if (!termsAccepted) {
    return <Navigate to="/terms" replace />;
  }

  const userRole = user?.role || 'viewer';
  if (!requiredRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Terms Route - Only accessible when authenticated but terms not yet accepted
const TermsRoute = () => {
  const { isAuthenticated, loading, mustChangePassword, termsAccepted } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Password change takes priority over terms
  if (mustChangePassword) {
    return <ChangePasswordModal />;
  }

  // If terms already accepted, redirect to dashboard
  if (termsAccepted) {
    return <Navigate to="/" replace />;
  }

  return <TermsPage />;
};

// Assessments route - shows Coming Soon or Admin Preview based on role/flags
const AssessmentsRoute = () => {
  const { isAuthenticated, loading, user, mustChangePassword, termsAccepted } = useAuth();
  const { canAccessAssessments, isAssessmentsComingSoon } = useFeatureFlags();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <ChangePasswordModal />;
  }

  // Force terms acceptance if not accepted
  if (!termsAccepted) {
    return <Navigate to="/terms" replace />;
  }

  // Super Admin gets full preview
  if (user?.role === 'super_admin' && canAccessAssessments()) {
    return (
      <AppLayout>
        <AssessmentsAdminPreviewPage />
      </AppLayout>
    );
  }

  // Everyone else sees Coming Soon
  return (
    <AppLayout>
      <AssessmentsComingSoonPage />
    </AppLayout>
  );
};

// Layout with Sidebar - uses user.id as key to force remount on user change
const AppLayout = ({ children }) => {
  const { user } = useAuth();
  
  // Key forces complete remount when user changes, clearing all stale state
  return (
    <div className="app-layout" key={user?.id || 'anonymous'}>
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

// Auth Route Wrapper (redirects to dashboard if already logged in)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />
      
      {/* Forgot Password - Public */}
      <Route
        path="/forgot-password"
        element={
          <AuthRoute>
            <ForgotPasswordPage />
          </AuthRoute>
        }
      />
      
      {/* Reset Password - Public */}
      <Route
        path="/reset-password"
        element={
          <AuthRoute>
            <ResetPasswordPage />
          </AuthRoute>
        }
      />

      {/* Explore Page - Public product tour (no auth required) */}
      <Route
        path="/explore"
        element={<ExplorePage />}
      />

      {/* Terms Acceptance - Requires auth but not terms acceptance */}
      <Route
        path="/terms"
        element={<TermsRoute />}
      />

      {/* Protected Routes - All authenticated users */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StudentsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/students/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StudentProfilePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ReportsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Editor Routes - Super Admin, Admin and Specialist */}
      <Route
        path="/students/new"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin', 'specialist']}>
            <AppLayout>
              <AddStudentPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/quick-entry"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin', 'specialist']}>
            <AppLayout>
              <QuickEntryPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/exports"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin', 'specialist']}>
            <AppLayout>
              <ExportsPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/import"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin', 'specialist']}>
            <AppLayout>
              <ImportPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/users"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin']}>
            <AppLayout>
              <UsersPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/staff"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin']}>
            <AppLayout>
              <StaffDirectoryPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/schools"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin']}>
            <AppLayout>
              <SchoolsPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <RoleProtectedRoute requiredRoles={['super_admin', 'admin']}>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </RoleProtectedRoute>
        }
      />

      {/* Assessments Module */}
      <Route
        path="/assessments"
        element={<AssessmentsRoute />}
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureFlagsProvider>
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              duration: 3000,
            }}
          />
          <AppRoutes />
        </FeatureFlagsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
